import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean | null;
  adminRole: string | null;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: AuthError | null; user: User | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [adminRole, setAdminRole] = useState<string | null>(null);

  // Check if user is admin by querying admin_users table directly
  const checkAdminStatus = async (userId: string) => {
    try {
      console.log('[AdminCheck] Checking admin status for user:', userId);

      // Query admin_users table directly via Supabase client
      const { data, error } = await supabase
        .from('admin_users')
        .select('role')
        .eq('user_id', userId)
        .single();

      if (error) {
        // PGRST116 means no rows found - user is not an admin
        if (error.code === 'PGRST116') {
          console.log('[AdminCheck] User is not an admin');
          setIsAdmin(false);
          setAdminRole(null);
          return;
        }
        // Other errors
        console.error('[AdminCheck] Error querying admin_users:', error);
        setIsAdmin(false);
        setAdminRole(null);
        return;
      }

      if (data) {
        console.log('[AdminCheck] User is admin with role:', data.role);
        setIsAdmin(true);
        setAdminRole(data.role);
      } else {
        setIsAdmin(false);
        setAdminRole(null);
      }
    } catch (err) {
      console.error('[AdminCheck] Failed to check admin status:', err);
      setIsAdmin(false);
      setAdminRole(null);
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(null);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(null);
        setAdminRole(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error, user: data?.user ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(null);
    setAdminRole(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, isAdmin, adminRole, signIn, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
